/**
 * Authentication Manager for API Services
 * 
 * Centralized authentication state management for service layer.
 * Provides user email for API calls without requiring React context in services.
 */

class AuthManager {
    private userEmail: string | null = null;
    private userGroups: string = '';

    /**
     * Set the current authenticated user's email
     * Called by AuthContext when user logs in
     */
    setUserEmail(email: string): void {
        this.userEmail = email;
    }

    /**
     * Set the current authenticated user's groups (comma-separated)
     * Called by AuthContext when user groups are fetched
     */
    setUserGroups(groups: string): void {
        this.userGroups = groups;
    }

    /**
     * Clear authentication data
     * Called by AuthContext when user logs out
     */
    clearAuth(): void {
        this.userEmail = null;
        this.userGroups = '';
    }

    /**
     * Get the current authenticated user's email
     * Throws error if no user is authenticated
     */
    getUserEmail(): string {
        if (!this.userEmail) {
            throw new Error('No authenticated user. Please log in first.');
        }
        return this.userEmail;
    }

    /**
     * Get the current authenticated user's groups as comma-separated string
     * Returns empty string if no groups available
     */
    getUserGroups(): string {
        return this.userGroups;
    }

    /**
     * Check if user is authenticated
     */
    isAuthenticated(): boolean {
        return this.userEmail !== null;
    }
}

// Singleton instance
export const authManager = new AuthManager();